import React from 'react';
import { toast } from 'react-toastify';

import DeleteConfirmModal from 'components/DeleteConfirmModal';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import PropTypes from 'prop-types';
import { formatCurrency, toastStyle } from 'utils/helper';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

const DeleteGroup = ({ refetch }) => {
  const {
    refetchGroup,
    showDeleteGroup,
    refetchTabs,

    delData,
    hideDeleteGroupModal,
    deleteGroupMutation,
    groupDetailsForModal,
    hasEditAccess,
  } = useHomeScreenContext();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const instanceType = queryParams.get('type') || 'budget';
  const queryClient = useQueryClient();

  const handleCancel = () => {
    hideDeleteGroupModal();
  };
  const getDeleteItemCount = () => {
    return delData?.reduce((i, n) => {
      return i + 1;
    }, 0);
  };

  const generateDeleteMessage = () => {
    const fringeCount = getDeleteItemCount();
    const baseMessage = `Are you sure you want to delete ${fringeCount} ${
      fringeCount === 1 ? 'group' : 'groups'
    }?`;

    // Check if any group has lines
    const groupsWithLines = groupDetailsForModal?.data?.filter(
      group => group?.lines?.length > 0
    );

    if (groupsWithLines?.length > 0 && instanceType === 'budget') {
      return (
        <>
          <div style={{ textAlign: 'left' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ⚠️ Warning! The following groups are currently applied to line
              items:
            </p>
            {groupsWithLines.map((group, groupIndex) => (
              <div key={groupIndex} style={{ marginBottom: '16px' }}>
                <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  Group: {group.name || `Group ${groupIndex + 1}`}
                </p>
                <ul
                  style={{
                    listStyleType: 'disc',
                    paddingLeft: '1.2rem',
                    textAlign: 'left',
                    display: 'inline-block',
                  }}
                >
                  {group?.lines?.map((line, lineIndex) => (
                    <li key={lineIndex}>{line}</li>
                  ))}
                </ul>
                <p
                  style={{
                    marginTop: '8px',
                    fontWeight: 500,
                    marginBottom: '8px',
                    textAlign: 'left',
                  }}
                >
                  Totaling:{' '}
                  <strong>{formatCurrency(group?.updated_total)}</strong>.
                </p>
              </div>
            ))}
            <p style={{ marginTop: '8px' }}>
              Deleting these groups will remove them from all of the above line
              items and reduce the budget by the respective amounts.
            </p>
          </div>
        </>
      );
    }

    // Default message if no groups have lines
    return <p>{baseMessage}</p>;
  };

  const deleteMessage = (() => {
    const isSingle = delData?.length === 1;
    const hasLines = groupDetailsForModal?.data?.[0]?.lines?.length > 0;
    const name =
      groupDetailsForModal?.data?.[0]?.full_name ||
      delData?.[0]?.full_name ||
      'this group';

    if (isSingle) {
      if (hasLines && instanceType === 'budget') {
        return (
          <div style={{ textAlign: 'left' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ⚠️ Warning! This group is currently applied to:
            </p>
            <ul
              style={{
                listStyleType: 'disc',
                paddingLeft: '1.2rem',
                textAlign: 'left',
                display: 'inline-block',
              }}
            >
              {groupDetailsForModal?.data[0]?.lines?.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
            <p
              style={{
                marginTop: '0px',
                fontWeight: 500,
                marginBottom: '15px',
                textAlign: 'left',
              }}
            >
              Totaling:{' '}
              <strong>
                {formatCurrency(groupDetailsForModal?.data[0]?.updated_total)}
              </strong>
              .
            </p>
            <p style={{ marginTop: '8px' }}>
              Deleting it will remove it from all of those line items and reduce
              the budget by that amount.
            </p>
          </div>
        );
      } else {
        return (
          <p>
            Are you sure you want to delete <strong>{name}</strong>?
          </p>
        );
      }
    }

    // If multiple groups
    return generateDeleteMessage();
  })();

  const handleDeleteBudget = async () => {
    const groupsIds = [];

    for (let i = 0; i < delData?.length; i++) {
      const id = Number(delData[i]?.id);
      groupsIds.push(id);
    }
    try {
      await deleteGroupMutation.mutateAsync({
        group_Ids: groupsIds,
      });

      toast.dismiss();
      toast.success(
        `Group "${delData[0]?.full_name}" deleted successfully`,
        toastStyle
      );
      queryClient.invalidateQueries({
        predicate: query => query.queryKey[0] === 'get-group-fringe-detail',
      });
      handleCancel();
      refetchGroup?.();
      refetchTabs();
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.non_field_errors?.[0];
      toast.dismiss();
      toast.error(
        message ||
          `An error occurred while deleting the budget "${delData[0]?.full_name}". Please try again later`,
        toastStyle
      );
    }
  };

  return (
    <DeleteConfirmModal
      showModal={showDeleteGroup}
      handleOnClose={handleCancel}
      onCancel={handleCancel}
      onConfirm={handleDeleteBudget}
      confirmText={deleteMessage}
      confirmLabel="Delete"
      disabled={instanceType === 'budget' && !hasEditAccess()}
    />
  );
};
DeleteGroup.propTypes = {
  refetch: PropTypes.func.isRequired, // Adjust the type based on the actual type of refetch
};

export default DeleteGroup;
